import { HeaderPage } from "@/components/HeaderPage";
import { CardContent } from "@/components/CardContent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Loader2, Upload, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useRef } from "react";
import { useCreateBanner } from "@/hooks/useBanners";
import { useGeneratePresignedUrl, uploadFileToR2 } from "@/hooks/useMedia";
import toast from "react-hot-toast";
import type { BannerType } from "@/types/Banner";

export const CreateBanner = () => {
  const navigate = useNavigate();
  const createBanner = useCreateBanner();
  const generatePresignedUrl = useGeneratePresignedUrl();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<BannerType>("DESKTOP");
  const [isActive, setIsActive] = useState(true);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkTarget, setLinkTarget] = useState<"_blank" | "_self">("_blank");
  const [scrollTime, setScrollTime] = useState("5");
  const [imageKey, setImageKey] = useState("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Por favor, selecione apenas arquivos de imagem.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("A imagem deve ter no máximo 5MB.");
      return;
    }

    setUploading(true);

    try {
      const presignedData = await generatePresignedUrl.mutateAsync({
        fileName: file.name,
        mimeType: file.type,
        type: "IMAGE",
        size: file.size,
      });

      await uploadFileToR2(presignedData.uploadUrl, file);

      setImageKey(presignedData.key);
      setImagePreview(URL.createObjectURL(file));
      toast.success("Imagem carregada com sucesso!");
    } catch (error) {
      console.error("Erro ao fazer upload:", error);
      toast.error("Erro ao carregar imagem.");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setImageKey("");
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("O título é obrigatório.");
      return;
    }

    if (!imageKey) {
      toast.error("A imagem do banner é obrigatória.");
      return;
    }

    setIsSubmitting(true);

    try {
      await createBanner.mutateAsync({
        title: title.trim(),
        description: description.trim() || undefined,
        key: imageKey,
        type,
        isActive,
        linkUrl: linkUrl.trim() || undefined,
        linkTarget: linkUrl.trim() ? linkTarget : undefined,
        scrollTime: Number(scrollTime) || 5,
      });

      toast.success("Banner criado com sucesso!");
      navigate("/banners");
    } catch (error: unknown) {
      console.error("Erro ao criar banner:", error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      const errorMessage =
        axiosError.response?.data?.message || "Erro ao criar banner";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/banners")}
          className="h-8 w-8"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <HeaderPage
          title="Novo Banner"
          description="Crie um novo banner para exibir aos usuários"
        />
      </div>

      <form onSubmit={handleSubmit}>
        <CardContent
          title="Informações do Banner"
          content={
            <div className="space-y-6">
              {/* Upload de Imagem */}
              <div className="space-y-2">
                <Label>Imagem do Banner *</Label>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-6">
                  {imagePreview ? (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Preview do banner"
                        className="w-full max-h-64 object-contain rounded-lg"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8"
                        onClick={handleRemoveImage}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div
                      className="flex flex-col items-center justify-center cursor-pointer py-8"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploading ? (
                        <Loader2 className="h-10 w-10 text-gray-400 animate-spin" />
                      ) : (
                        <>
                          <Upload className="h-10 w-10 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">
                            Clique para selecionar uma imagem
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            PNG, JPG ou WebP (máx. 5MB)
                          </p>
                        </>
                      )}
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageSelect}
                  />
                </div>
              </div>

              {/* Título */}
              <div className="space-y-2">
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  placeholder="Digite o título do banner"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              {/* Descrição */}
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  placeholder="Digite uma descrição para o banner (opcional)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Tipo */}
              <div className="space-y-2">
                <Label>Tipo de Banner *</Label>
                <Select
                  value={type}
                  onValueChange={(value) => setType(value as BannerType)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DESKTOP">Desktop</SelectItem>
                    <SelectItem value="MOBILE">Mobile</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Link */}
              <div className="space-y-2">
                <Label htmlFor="linkUrl">Link (opcional)</Label>
                <Input
                  id="linkUrl"
                  type="url"
                  placeholder="https://exemplo.com"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                />
              </div>

              {/* Link Target */}
              {linkUrl && (
                <div className="space-y-2">
                  <Label>Abrir link em</Label>
                  <Select
                    value={linkTarget}
                    onValueChange={(value) =>
                      setLinkTarget(value as "_blank" | "_self")
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_blank">Nova aba</SelectItem>
                      <SelectItem value="_self">Mesma aba</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Tempo de Rolagem */}
              <div className="space-y-2">
                <Label htmlFor="scrollTime">Tempo de rolagem (segundos)</Label>
                <Input
                  id="scrollTime"
                  inputMode="numeric"
                  placeholder="5"
                  value={scrollTime}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || /^\d+$/.test(val)) {
                      setScrollTime(val);
                    }
                  }}
                />
                <p className="text-xs text-gray-400">
                  Tempo em segundos que o banner permanecerá em tela antes de exibir o próximo. Padrão: 5s
                </p>
              </div>

              {/* Ativo */}
              <div className="flex items-center justify-between border rounded-lg p-4">
                <div>
                  <Label>Banner Ativo</Label>
                  <p className="text-sm text-gray-500">
                    Defina se o banner será exibido aos usuários
                  </p>
                </div>
                <Switch checked={isActive} onCheckedChange={setIsActive} />
              </div>

              {/* Botões */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/banners")}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="text-white bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))]"
                  disabled={isSubmitting || uploading}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    "Criar Banner"
                  )}
                </Button>
              </div>
            </div>
          }
        />
      </form>
    </>
  );
};
